import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
    EventsInfoWithFilteredPlayersDocument,
    EventsInfoWithFilteredPlayersQuery,
    EventsInfoWithFilteredPlayersQueryVariables,
} from '@/api/events/query.generated';
import { useLoadingEntryImportSheet } from '@/feature/alerts/components/LoadingEntryImportSheetProvider';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useLinking } from '@/feature/entry-share/hooks/use-linking';
import { useLineupUnavailableSheet } from '@/feature/entry-share/hooks/use-share-link-import';
import { useAddLineup } from '@/feature/entry-share/screens/AddLineupModalProvider';
import { logger } from '@/utils/logging';
import { useClient } from 'urql';

import {
    LOG_TAG,
    extractPartnerParam,
    extractPicksParam,
    extractReferralQueryString,
    isPartnerLink,
    mapPartnerPicksToSharedEntryPicks,
    parsePicksParam,
} from '../utils/partner-link-import-utils';

/**
 * Hook that handles deeplinks generated from Partner API (l.betr.app/partner/...).
 * Routes to the appropriate handler based on the action path:
 *  - /partner/betslip?picks=... → parse picks, fetch data, add to betslip
 */
export const usePartnerLinkImport = () => {
    const { showToast } = useAlerts();
    const { showAddLineupModal } = useAddLineup();
    const { t } = useTranslation(['lineup_update_modal']);
    const gqlClient = useClient();
    const { showImportingEntrySheet, dismissImportingEntrySheet } = useLoadingEntryImportSheet();

    const showLineupUnavailable = useLineupUnavailableSheet();

    const importPartnerBetslip = useCallback(
        async (queryString: string) => {
            const partner = extractPartnerParam(queryString);
            logger.info(LOG_TAG, `Importing betslip from partner deeplink, partner: ${partner}`);

            const picksParam = extractPicksParam(queryString);
            if (!picksParam) {
                showToast({ message: 'Invalid link', toastType: 'warning' });
                return;
            }
            let picks;
            try {
                picks = parsePicksParam(picksParam);
            } catch {
                showToast({ message: 'Invalid link', toastType: 'warning' });
                return;
            }
            if (picks.length === 0) {
                showToast({ message: 'Invalid link', toastType: 'warning' });
                return;
            }

            try {
                showImportingEntrySheet({
                    title: t('importing_lineup_title'),
                    description: t('importing_lineup_description'),
                });
                const response = await gqlClient.query<
                    EventsInfoWithFilteredPlayersQuery,
                    EventsInfoWithFilteredPlayersQueryVariables
                >(
                    EventsInfoWithFilteredPlayersDocument,
                    {
                        eventIds: picks.map(p => p.eventId),
                        playerIds: picks.map(p => p.playerId),
                    },
                    { requestPolicy: 'network-only' }
                );
                const events = response.data?.getEventsWithFilteredPlayers ?? [];
                const mappedPicks = mapPartnerPicksToSharedEntryPicks(picks, events);

                if (mappedPicks.length === 0) {
                    showLineupUnavailable();
                } else {
                    logger.info(LOG_TAG, `Showing partner lineup modal with ${mappedPicks.length} picks`);
                    showAddLineupModal({ lineup: mappedPicks, events, refferer: { partnerName: partner } });
                }
            } catch (error) {
                showLineupUnavailable();
                logger.warn(LOG_TAG, error);
            } finally {
                dismissImportingEntrySheet();
            }
        },
        [
            dismissImportingEntrySheet,
            gqlClient,
            showAddLineupModal,
            showImportingEntrySheet,
            showLineupUnavailable,
            showToast,
            t,
        ]
    );

    const handlePartnerLink = useCallback(
        (url: string) => {
            if (!isPartnerLink(url)) {
                return;
            }

            const queryString = extractReferralQueryString(url);
            if (!queryString) {
                showToast({ message: 'Invalid link', toastType: 'warning' });
                return;
            }

            importPartnerBetslip(queryString);
        },
        [importPartnerBetslip, showToast]
    );

    useLinking({
        source: 'partner-link-import',
        onInitialLink: handlePartnerLink,
        onNewLink: handlePartnerLink,
    });
};
